import React, { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useSnackbar, SnackbarKey } from 'notistack';
import './TransfersReturns.scss';
import { formatEndpointAddressHtml } from 'src/util/formatters';
import {
    ShippingLabelTypes,
    getManifestedOrUnmanifestedData,
    getManifestById,
    generatePDF,
    deleteParcels,
} from 'src/api/shippingPortal';
import {
    ShippingPortalManifest,
    ShippingPortalParcel,
    SortDirection,
    ShippingPortalPrintItem,
    DateFormats,
    ShippingPortalSortingOptions,
    ShippingPortalFilters,
    CustomerInfo,
    ShippingPortalSortOrder,
} from '@lanterds/types';
import { getCapitalized } from 'src/util/helpers';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { CellContext, ExpandedState, Header as TableHeader, Row, ColumnDef } from '@tanstack/react-table';
import IndeterminateCheckbox from 'src/components/common/FmtpTable/IndeterminiteCheckbox';
import { openPDFInNewTab } from 'src/util/openPdf';
import { ParcelMap, SelectedRows, ShippingPortalDataItem } from '../types';
import dayjs from 'dayjs';
import { allCustomersDatum, allCustomersValue, PAGE_INFO } from 'src/util/constants';
import Header from 'src/components/common/Header/Header';
import { useFmtpNavigate } from 'src/hooks/useFmtpNavigate';
import Grid from '@mui/material/Unstable_Grid2';
import {
    Box,
    Card,
    CardContent,
    FormControl,
    Typography,
    Button,
    Autocomplete,
    TextField,
    CircularProgress,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';

import { CheckBox, CheckBoxOutlineBlank } from '@mui/icons-material';
import EmptyTreeNode from 'src/components/common/FmtpTable/EmtpyTreeNode';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

enum DataSet {
    Unmanifested = 'unmanifested',
    Manifested = 'manifested',
}

export default function TransfersReturns({
    customersAreLoading,
    customers,
    selectedCustomer,
    setSelectedCustomer,
    shippingType,
}: {
    customersAreLoading: boolean;
    customers: Array<CustomerInfo>;
    selectedCustomer: { id: string; label: string } | null;
    setSelectedCustomer: (value: { id: string; label: string } | null) => void;
    shippingType: ShippingLabelTypes;
}) {
    const fmtpNavigate = useFmtpNavigate();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const initialFilters: ShippingPortalFilters = {
        pageSize: 100,
        sortOrder: {
            column: ShippingPortalSortingOptions.createdOn,
            order: SortDirection.DESCENDING,
        },
        type: shippingType,
    };

    const [clearFilters, setClearFilters] = useState(false);
    const [fetchedAllUnmanifestedLabels, setFetchedAllUnmanifestedLabels] = useState<boolean>(false);
    const [fetchedAllManifests, setFetchedAllManifests] = useState<boolean>(false);
    const [hideUnmanifestedParcels, setHideUnmanifestedParcels] = useState<boolean>(false);
    const [filters, setFilters] = useState<ShippingPortalFilters>(initialFilters);
    const [canSelectUnmanifestedParcelRow, setCanSelectUnmanifestedParcelRow] = useState<boolean>(true);
    const [canSelectManifestRow, setCanSelectManifestRow] = useState<boolean>(true);
    const [canSelectManifestedParcelRow, setCanSelectManifestedParcelRow] = useState<boolean>(true);
    const [fetchedNestedParcels, setFetchedNestedParcels] = useState<ParcelMap>({});
    const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
    const [clearSelections, setClearSelections] = useState<boolean>(false);
    const [sortOrder, setSortOrder] = useState(initialFilters.sortOrder);
    const [selectedRows, setSelectedRows] = useState<SelectedRows>({});
    const [isGeneratingLabel, setIsGeneratingLabel] = useState<boolean>(false);
    const [isGeneratingManifest, setIsGeneratingManifest] = useState<boolean>(false);
    const [unmanifestedParcelData, setUnmanifestedParcelData] = useState<ShippingPortalDataItem[]>([]);
    const [manifestedParcelData, setManifestedParcelData] = useState<ShippingPortalDataItem[]>([]);
    const fetchSnackbarRef = useRef<SnackbarKey | null>(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [deleteLabelsDialogOpen, setDeleteLabelsDialogOpen] = useState(false);

    const getDataSet = () => {
        let dataSet = DataSet.Unmanifested;

        if (fetchedAllUnmanifestedLabels || filters['manifestNumber'] || filters['departureDate'] || hideUnmanifestedParcels) {
            dataSet = DataSet.Manifested;
        } else if (
            sortOrder.column === ShippingPortalSortingOptions.manifestNumber ||
            sortOrder.column === ShippingPortalSortingOptions.departureDate
        ) {
            if (sortOrder.order === SortDirection.DESCENDING) {
                if (!fetchedAllManifests) {
                    dataSet = DataSet.Manifested;
                }
            } else {
                // sort order is ascending
                if (fetchedAllUnmanifestedLabels) {
                    dataSet = DataSet.Manifested;
                }
            }
        }

        return dataSet;
    };

    const dataSet = getDataSet();

    const { data, fetchNextPage, isFetching } = getManifestedOrUnmanifestedData(dataSet, filters, [
        dataSet,
        filters,
        hideUnmanifestedParcels,
    ]);

    // Handle states when we've fetched all data from an API
    useEffect(() => {
        const totalPages = data?.pages?.length ?? 0;
        const isFinishedWithDataset = data?.pages[totalPages - 1].length < initialFilters.pageSize;

        if (data) {
            if (dataSet === DataSet.Unmanifested) {
                setUnmanifestedParcelData(data.pages);
            } else {
                setManifestedParcelData(data.pages);
            }
        }

        if (data && isFinishedWithDataset) {
            if (dataSet === DataSet.Unmanifested) {
                setFetchedAllUnmanifestedLabels(true);
            } else {
                setFetchedAllManifests(true);
            }
        }
    }, [data]);

    useEffect(() => {
        setFilters({ ...filters, sortOrder });
    }, [sortOrder]);

    useEffect(() => {
        setFilters({ ...initialFilters, type: shippingType });
        resetCommonStates();
    }, [shippingType]);

    // Need to reset state after passing to fmtpTable and setting filter states
    useEffect(() => {
        if (clearFilters) {
            setClearFilters(false);
        }
    }, [clearFilters]);

    // Need to reset state after passing to fmtpTable and setting filter states
    useEffect(() => {
        if (clearSelections) {
            setClearSelections(false);
        }
    }, [clearSelections]);

    const mapData = (data: ShippingPortalDataItem[], parentRowId?: string): ShippingPortalDataItem[] => {
        const list = data?.map((dataItem: ShippingPortalDataItem, index: number) => {
            const {
                manifestId,
                manifestNumber,
                parcelNumber,
                reference,
                createdBy,
                createdOn,
                numberOfParcels,
                origin,
                destination,
                departureDate,
            } = dataItem ?? {};

            let subRows = [];

            if (numberOfParcels) {
                subRows = new Array(numberOfParcels);
                subRows.fill({}, 0, numberOfParcels);
            } else if (fetchedNestedParcels[manifestId]) {
                subRows = fetchedNestedParcels[manifestId];
            }

            return {
                ...dataItem,
                rowId: parentRowId ? `${parentRowId}.${index}` : `${index}`,
                manifestNumber: manifestNumber ? manifestNumber : '',
                parcelNumber: parcelNumber ? parcelNumber : '',
                reference: reference ? reference : '',
                createdBy: createdBy ? createdBy : '',
                createdOn: createdOn ? createdOn : '',
                departureDate: departureDate ? departureDate : '',
                origin: origin,
                destination: destination,
                subRows,
            };
        });

        return list as ShippingPortalDataItem[];
    };

    // Data must be memoized for reactTable component, which allows us cached data feature and other enhancements if desired
    const flatData = useMemo(() => {
        const flattendedUnmanifestedData = unmanifestedParcelData.flatMap((page) => page) ?? [];
        const flattendedManifestedData = manifestedParcelData.flatMap((page) => page) ?? [];
        let flattenedData = [];

        // As long as there is a value in the 'manifestNumber' or 'departureDate' filter, only show manifested data
        if (filters['manifestNumber'] || filters['departureDate']) {
            setFetchedAllUnmanifestedLabels(true);
            flattenedData = flattendedManifestedData;
        } else {
            // Only show manifested data when Hide Unmanifested Parcels is checked
            if (hideUnmanifestedParcels) {
                flattenedData = flattendedManifestedData;
            } else {
                // Sorting 'manifestNumber' or 'departureDate' column treats unmanifested parcels as empty values,
                // so manifested data will be at the top on desc
                // and unmanifested data will be at the top on asc
                if (
                    (filters.sortOrder.column === ShippingPortalSortingOptions.manifestNumber ||
                        filters.sortOrder.column === ShippingPortalSortingOptions.departureDate) &&
                    filters.sortOrder.order === SortDirection.DESCENDING
                ) {
                    flattenedData = [...flattendedManifestedData, ...flattendedUnmanifestedData];
                } else {
                    // Every other column will always have unmanifested parcels at the top,
                    // whether they have been sorted or filtered. Or if the sortOrder.column
                    // is 'manifestNumber' or 'departureDate' and the sort direction is asc
                    flattenedData = [...flattendedUnmanifestedData, ...flattendedManifestedData];
                }
            }
        }
        return mapData(flattenedData);
    }, [unmanifestedParcelData, manifestedParcelData]);

    const handleCreateLabelClick = () => {
        fmtpNavigate(`${PAGE_INFO.shippingPortal.href}/label/new/?type=${shippingType}`);
    };

    const deleteSnackbarRef = useRef<SnackbarKey | null>(null);

    const handleConfirmDeleteLabels = async () => {
        setDeleteLabelsDialogOpen(false);
        deleteSnackbarRef.current = enqueueSnackbar('Deleting selected labels…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        try {
            const parcelIds = Object.keys(selectedRows).map((rowId) => flatData[Number(rowId)].id);
            await deleteParcels(parcelIds);
            enqueueSnackbar('Labels deleted successfully.', {
                variant: 'success',
                preventDuplicate: true,
            });
            setClearFilters(true);
            setFilters(initialFilters);
            setHideUnmanifestedParcels(false);
            resetCommonStates();
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to delete labels.', {
                variant: 'error',
                preventDuplicate: true,
            });
        } finally {
            if (deleteSnackbarRef.current) {
                closeSnackbar(deleteSnackbarRef.current);
                deleteSnackbarRef.current = null;
            }
        }
    };

    const onDeleteLabels = async () => {
        if (!canDeleteLabels()) {
            enqueueSnackbar('Please select an unmanifested parcel before deleting.', {
                variant: 'warning',
                preventDuplicate: true,
            });
            return;
        }
        setDeleteLabelsDialogOpen(true);
    };

    const labelSnackbarRef = useRef<SnackbarKey | null>(null);

    const onClickPrintLabels = async () => {
        if (!canPrint()) {
            enqueueSnackbar('Please select a label before printing.', {
                variant: 'warning',
                preventDuplicate: true,
            });
            return;
        }

        labelSnackbarRef.current = enqueueSnackbar('Generating labels PDF…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        try {
            const selectedRowIds = Object.keys(selectedRows);
            const labelsToPrint: ShippingPortalPrintItem[] = [];
            setIsGeneratingLabel(true);

            for (const rowId of selectedRowIds) {
                // This should always have a value, corresponding to the manifest (parent) row
                const manifestRowNumber = rowId.split('.')[0];
                // If parcelRowNumber has a value, we know the current row is a parcel (sub) row, otherwise it is a manifest row
                const parcelRowNumber = rowId.split('.')[1];
                const isCurrentRowParcel = !!parcelRowNumber;
                const isCurrentRowManifest = !isCurrentRowParcel;
                const manifest = flatData.find((item: ShippingPortalDataItem) => item.rowId === manifestRowNumber);

                if (manifest) {
                    const { manifestId } = manifest;
                    const { subRows = [] } = manifest;
                    let includeAllLabels = true;

                    // Determine whether all parcels will be included with the manifest
                    for (let i = 0; i < subRows?.length; i++) {
                        const isSubRowSelected = !!selectedRows[`${manifestRowNumber}.${i}`];
                        if (!isSubRowSelected) {
                            includeAllLabels = false;
                            break;
                        }
                    }

                    // We still want to potentially add a manifest type to labelsToPrint because a user could
                    // select a manifest row without expanding the row first, meaning we need to print all
                    // labels of that manifest
                    if (isCurrentRowManifest) {
                        if (includeAllLabels) {
                            labelsToPrint.push({
                                type: 'manifest',
                                id: manifestId.toString(),
                                includeAllLabels: true,
                                excludeManifest: true,
                            });
                        }
                    } else {
                        const isManifestRowSelected = !!selectedRows[`${manifestRowNumber}`];

                        // Skip processing label when a manifest and all its labels should be printed
                        if (!(isManifestRowSelected && includeAllLabels)) {
                            const currentParcelRowIndex = Number(parcelRowNumber);
                            if (typeof currentParcelRowIndex === 'number' && manifest.subRows) {
                                const parcel = manifest.subRows[currentParcelRowIndex];
                                let id = '';

                                if ('id' in parcel) {
                                    id = parcel.id;
                                } else {
                                    // Need to fetch parcels since user has not expanded manifest row
                                    const manifestWithParcels = await getManifestById(manifestId);
                                    const { parcels = [] } = manifestWithParcels;
                                    const parcel = parcels[currentParcelRowIndex];
                                    id = parcel.id;
                                }

                                labelsToPrint.push({ type: 'label', id });
                            }
                        }
                    }
                }
            }
            if (labelsToPrint.length) {
                const response = await generatePDF(labelsToPrint);
                if (response.pdfBase64) {
                    openPDFInNewTab(response.pdfBase64);
                    enqueueSnackbar('Labels PDF ready.', { variant: 'success', preventDuplicate: true });
                } else {
                    throw new Error('No PDF returned');
                }
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to generate labels PDF.', { variant: 'error', preventDuplicate: true });
        } finally {
            if (labelSnackbarRef.current) {
                closeSnackbar(labelSnackbarRef.current);
                labelSnackbarRef.current = null;
            }
            setIsGeneratingLabel(false);
        }
    };

    const manifestSnackbarRef = useRef<SnackbarKey | null>(null);

    const onClickPrintManifests = async () => {
        if (!canPrint()) {
            enqueueSnackbar('Please select a manifest before printing.', {
                variant: 'warning',
                preventDuplicate: true,
            });
            return;
        }

        manifestSnackbarRef.current = enqueueSnackbar('Generating manifests PDF…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        try {
            const selectedRowIds = Object.keys(selectedRows);
            const manifestsToPrint: ShippingPortalPrintItem[] = [];
            setIsGeneratingManifest(true);

            for (const rowId of selectedRowIds) {
                // This should always have a value, corresponding to the manifest (parent) row
                const manifestRowNumber = rowId.split('.')[0];
                // If parcelRowNumber has a value, we know the current row is a parcel (sub) row, otherwise it is a manifest row
                const parcelRowNumber = rowId.split('.')[1];
                const isCurrentRowParcel = !!parcelRowNumber;
                const isCurrentRowManifest = !isCurrentRowParcel;
                const manifest = flatData.find((item: ShippingPortalDataItem) => item.rowId === manifestRowNumber);

                if (manifest) {
                    const { manifestId } = manifest;

                    if (isCurrentRowManifest) {
                        manifestsToPrint.push({
                            type: 'manifest',
                            id: manifestId.toString(),
                            includeAllLabels: false,
                            excludeManifest: false,
                        });
                    }
                }
            }
            if (manifestsToPrint.length) {
                const response = await generatePDF(manifestsToPrint);
                if (response.pdfBase64) {
                    openPDFInNewTab(response.pdfBase64);
                    enqueueSnackbar('Manifests PDF ready.', { variant: 'success', preventDuplicate: true });
                } else {
                    throw new Error('No PDF returned');
                }
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to generate manifests PDF.', { variant: 'error', preventDuplicate: true });
        } finally {
            if (manifestSnackbarRef.current) {
                closeSnackbar(manifestSnackbarRef.current);
                manifestSnackbarRef.current = null;
            }
            setIsGeneratingManifest(false);
        }
    };

    const onFilterChange = (name: string, value: string | number | boolean) => {
        setFilters({ ...filters, [name]: value });
        resetCommonStates();
    };

    const onSortChange = (header: TableHeader<ShippingPortalDataItem, unknown>) => {
        const { sortOrder } = filters ?? {};
        const { column, order } = sortOrder ?? {};
        const { column: headerCol } = header;
        const { columnDef }: { columnDef: ColumnDef<ShippingPortalDataItem> } = headerCol;
        const { meta } = columnDef ?? {};

        const sortName = meta?.sortingOptionName as ShippingPortalSortingOptions;

        if (sortName) {
            // set sort column and direction
            if (sortName === column) {
                const newOrder = order === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;
                setSortOrder({
                    column: sortName,
                    order: newOrder,
                });
                header.column.toggleSorting(newOrder === SortDirection.DESCENDING);
            } else {
                setSortOrder({
                    column: sortName,
                    order: SortDirection.DESCENDING,
                } as ShippingPortalSortOrder);
                header.column.toggleSorting(true);
            }
        }

        resetCommonStates();
    };

    const onClickClearFilters = () => {
        setClearFilters(true);
        setFilters(initialFilters);
        setHideUnmanifestedParcels(false);
        resetCommonStates();
    };

    const onHideUnmanifestedParcelsChange = (e: ChangeEvent<HTMLInputElement>) => {
        setHideUnmanifestedParcels(e.target.checked);
        setFilters({ ...filters });
        resetCommonStates();
        // Need to keep below resetCommonStates()
        setFetchedAllUnmanifestedLabels(e.target.checked);
    };

    const onChangeCustomer = (value: { label: string; id: string } | null) => {
        if (value) {
            setSelectedCustomer(value);
            setFilters({
                ...initialFilters,
                type: shippingType,
                customerFmtpId: value.id !== allCustomersDatum.customerId ? value.id : null,
            });
        } else {
            setSelectedCustomer(allCustomersValue);
            setFilters({
                ...initialFilters,
                type: shippingType,
                customerFmtpId: null,
            });
        }

        setClearFilters(true);
        resetCommonStates();
    };

    const resetCommonStates = () => {
        setFetchedAllUnmanifestedLabels(false);
        setFetchedAllManifests(false);
        setFetchedNestedParcels({});
        setExpandedRows({});
        setSelectedRows({});
        setUnmanifestedParcelData([]);
        setManifestedParcelData([]);
    };

    const canCreateNewManifest = () => {
        if (!canSelectManifestRow && !canSelectManifestedParcelRow && canSelectUnmanifestedParcelRow) {
            return true;
        }

        return false;
    };

    const canPrint = () => {
        const selections = Object.keys(selectedRows);

        if (canSelectManifestRow && canSelectManifestedParcelRow && selections.length) {
            return true;
        }
        return false;
    };

    const canDeleteLabels = () => {
        const selections = Object.keys(selectedRows);

        if (!canSelectManifestRow && !canSelectManifestedParcelRow && selections.length) {
            return true;
        }
        return false;
    };

    const onClickCreateManifest = () => {
        if (canCreateNewManifest()) {
            const keys = Object.keys(selectedRows);
            const labelsToAddToManifest: string[] = [];

            for (let i = 0; i < keys.length; i++) {
                const currentSelection = keys[i];

                const unmanifestedParcel = flatData.find((item: ShippingPortalDataItem) => item.rowId === currentSelection);
                if (unmanifestedParcel) {
                    labelsToAddToManifest.push(unmanifestedParcel.id);
                }
            }

            if (labelsToAddToManifest.length) {
                fmtpNavigate(`${PAGE_INFO.shippingPortal.href}/manifest/new?type=${shippingType}`, {
                    state: { ids: labelsToAddToManifest },
                });
            } else {
                fmtpNavigate(`${PAGE_INFO.shippingPortal.href}/manifest/new?type=${shippingType}`);
            }
        } else {
            enqueueSnackbar('Please select an unmanifested parcel before creating a new manifest.', {
                variant: 'warning',
                preventDuplicate: true,
            });
        }
    };

    const enableRowSelection = (row: Row<ShippingPortalDataItem>) => {
        if (!row.getIsSelected()) {
            const isManifest = !!row.original.numberOfParcels;
            const isParcel = !!row.original.parcelNumber;

            const isUnmanifestedParcel = !!(isParcel && !row.original.manifest);
            const isManifestedParcel = !!(isParcel && row.original.manifest);

            if ((isManifest && !canSelectManifestRow) || (isManifestedParcel && !canSelectManifestedParcelRow)) {
                return false;
            }

            if (isUnmanifestedParcel && !canSelectUnmanifestedParcelRow) {
                return false;
            }
        }

        return true;
    };

    const getSubRows = (row: ShippingPortalDataItem) => {
        return row.subRows;
    };

    const createExpandRows = (row: Row<ShippingPortalDataItem>): React.JSX.Element[] => {
        const manifestId = row.original.manifestId ?? 'undefined';
        const departureDate = row.original.departureDate ?? 'undefined';

        const subRowsHtml = fetchedNestedParcels[manifestId]?.map((parcel: ShippingPortalParcel, index: number) => {
            const subRow = row.subRows[index];
            return (
                <tr className="body-row" key={`${index}.${parcel.parcelNumber}`}>
                    <td className="body-cell body-cell-select">
                        <div className="checkbox-cell">
                            <IndeterminateCheckbox
                                {...{
                                    checked: subRow.getIsSelected(),
                                    disabled: !row.getCanSelect(),
                                    indeterminate: subRow.getIsSomeSelected(),
                                    onChange: subRow.getToggleSelectedHandler(),
                                }}
                            />
                        </div>
                    </td>
                    <td className="body-cell body-cell-manifestNumber">
                        {/* <div className={'body-cell-expandable subRow-expanded'}>
                            <div className="expanded">
                                <span className="material-icons">subdirectory_arrow_right</span>
                            </div>
                        </div> */}
                        <EmptyTreeNode row={subRow} />
                    </td>
                    <td className="body-cell body-cell-parcelNumber">
                        <Typography
                            className="link"
                            onClick={() =>
                                fmtpNavigate(`${PAGE_INFO.shippingPortal.href}/label/${parcel.id}?type=${shippingType}`)
                            }
                        >
                            {parcel.parcelNumber}
                        </Typography>
                    </td>
                    <td className="body-cell body-cell-reference">{parcel.reference}</td>
                    <td className="body-cell body-cell-origin">{formatEndpointAddressHtml(parcel.origin, true)}</td>
                    <td className="body-cell body-cell-destination">{formatEndpointAddressHtml(parcel.destination, true)}</td>
                    <td className="body-cell body-cell-createdBy">{parcel.createdBy}</td>
                    <td className="body-cell body-cell-createdOn">{dayjs(parcel.createdOn).format(DateFormats.ShortDate)}</td>
                    <td className="body-cell body-cell-departureDate">{dayjs(departureDate).format(DateFormats.ShortDate)}</td>
                </tr>
            );
        });
        return subRowsHtml;
    };

    const onExpandedChange = useCallback(
        async (row: () => ExpandedState) => {
            const rowNumber = Object.keys(row())[0];
            const manifestRow = flatData[Number(rowNumber)];
            const newExpandedList = { ...expandedRows };

            if (!manifestRow.manifestId) return null;

            if (!expandedRows[rowNumber]) {
                const manifest = await getManifestById(manifestRow.manifestId);

                if (!fetchedNestedParcels[manifest.manifestId]) {
                    const newParcels = { ...fetchedNestedParcels };
                    const manifestId = manifest.manifestId;
                    const manifestParcels = manifest.parcels ?? [];

                    const newData = mapData(manifestParcels as ShippingPortalDataItem[], manifestRow.rowId);

                    newParcels[manifestId] = newData;
                    setFetchedNestedParcels(newParcels);
                }

                newExpandedList[rowNumber] = true;
                setExpandedRows(newExpandedList);
            } else {
                newExpandedList[rowNumber] = false;
                setExpandedRows(newExpandedList);
            }
        },
        [flatData, fetchedNestedParcels, expandedRows],
    );

    // Selected rows have a rowId, which will either be a single number or decimal for subRows.
    // So we use that to check which type of row was selected, whether in flatData (manifests and unmanifested parcels)
    // or parcels (manifested parcels)
    useEffect(() => {
        const keys = Object.keys(selectedRows);

        let hasManifestedParcel = false;
        let hasUnmanifestedParcel = false;

        for (let i = 0; i < keys.length; i++) {
            const currentSelection = keys[i];
            const splitRowId = currentSelection.split('.');
            const isManifestedParcel = splitRowId.length > 1;

            if (isManifestedParcel) {
                hasManifestedParcel = true;
                break;
            } else {
                // Manifest or UnmanifestedParcel
                const parentRow = flatData.find((item: ShippingPortalDataItem) => item.rowId === currentSelection);

                if (parentRow) {
                    const isUnmanifestedParcel = !parentRow.numberOfParcels;
                    if (isUnmanifestedParcel) {
                        hasUnmanifestedParcel = true;
                        break;
                    }
                }
            }
        }

        if (hasManifestedParcel) {
            setCanSelectManifestedParcelRow(true);
            setCanSelectManifestRow(true);
            setCanSelectUnmanifestedParcelRow(false);
        } else if (hasUnmanifestedParcel) {
            setCanSelectManifestedParcelRow(false);
            setCanSelectManifestRow(false);
            setCanSelectUnmanifestedParcelRow(true);
        } else {
            setCanSelectManifestedParcelRow(true);
            setCanSelectManifestRow(true);
            setCanSelectUnmanifestedParcelRow(true);
        }
    }, [selectedRows]);

    // TODO: Enhance with prefetching and / or better fetching for smoother UX
    const fetchMoreOnBottomReached = useCallback(
        async (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                if (!(fetchedAllManifests && fetchedAllUnmanifestedLabels)) {
                    // Once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
                    const endOfEndpointData = scrollHeight - scrollTop - clientHeight < 300;
                    const totalPages = data?.pageParams.length ?? 0;
                    const isFinishedWithDataset = data?.pages[totalPages - 1].length === initialFilters.pageSize;
                    let isReady = false;

                    if (dataSet === DataSet.Manifested) {
                        if (manifestedParcelData.length === data?.pages.length) {
                            isReady = true;
                        }
                    } else {
                        if (unmanifestedParcelData.length === data?.pages.length) {
                            isReady = true;
                        }
                    }

                    if (endOfEndpointData && !isFetching && isFinishedWithDataset && isReady) {
                        setIsLoadingMore(true);
                        await fetchNextPage();
                        setIsLoadingMore(false);
                    }
                }
            }
        },
        [data, isFetching, unmanifestedParcelData, manifestedParcelData, fetchNextPage],
    );

    useEffect(() => {
        if (isFetching) {
            const message = isLoadingMore ? 'Fetching more records…' : 'Loading transfers & returns…';
            if (fetchSnackbarRef.current) closeSnackbar(fetchSnackbarRef.current);
            fetchSnackbarRef.current = enqueueSnackbar(message, {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        } else if (fetchSnackbarRef.current) {
            closeSnackbar(fetchSnackbarRef.current);
            fetchSnackbarRef.current = null;
            setIsLoadingMore(false);
        }
    }, [isFetching, isLoadingMore, enqueueSnackbar, closeSnackbar]);

    useEffect(() => {
        return () => {
            if (fetchSnackbarRef.current) {
                closeSnackbar(fetchSnackbarRef.current);
                fetchSnackbarRef.current = null;
            }
            if (deleteSnackbarRef.current) {
                closeSnackbar(deleteSnackbarRef.current);
                deleteSnackbarRef.current = null;
            }
            if (labelSnackbarRef.current) {
                closeSnackbar(labelSnackbarRef.current);
                labelSnackbarRef.current = null;
            }
            if (manifestSnackbarRef.current) {
                closeSnackbar(manifestSnackbarRef.current);
                manifestSnackbarRef.current = null;
            }
        };
    }, [closeSnackbar]);

    const getIsFetching = () => {
        const noDataInFirstSet = !flatData.length && (!fetchedAllUnmanifestedLabels || !fetchedAllManifests);
        const noDataInBothSets = !flatData.length && hideUnmanifestedParcels && !fetchedAllManifests;
        return isFetching || noDataInFirstSet || noDataInBothSets;
    };

    const manifestColumns: ColumnDef<ShippingPortalDataItem>[] = [
        {
            id: 'select',
            size: 30,
            enableColumnFilter: false,
        },
        {
            accessorKey: 'manifestNumber',
            header: 'Manifest Number',
            meta: {
                isExpandable: true,
                sortingOptionName: 'manifestNumber',
                innerHtml: ({ row, getValue }: CellContext<ShippingPortalDataItem, unknown>) => {
                    const { manifestId } = row.original;
                    return (
                        <>
                            {manifestId && (
                                <Typography
                                    className="link"
                                    onClick={() =>
                                        fmtpNavigate(
                                            `${PAGE_INFO.shippingPortal.href}/manifest/${manifestId}?type=${shippingType}`,
                                        )
                                    }
                                >
                                    {getValue() as ReactNode}
                                </Typography>
                            )}
                            {!manifestId && 'Unmanifested'}
                        </>
                    );
                },
            },
        },
        {
            accessorKey: 'parcelNumber',
            header: 'Barcode',
            enableSorting: false,
            meta: {
                innerHtml: ({ row, getValue }: CellContext<ShippingPortalDataItem, unknown>) => {
                    const { id, parcelNumber } = row.original;
                    if (parcelNumber) {
                        return (
                            <Typography
                                className="link"
                                onClick={() =>
                                    fmtpNavigate(`${PAGE_INFO.shippingPortal.href}/label/${id}?type=${shippingType}`)
                                }
                            >
                                {getValue() as ReactNode}
                            </Typography>
                        );
                    } else {
                        return null;
                    }
                },
            },
        },
        {
            accessorKey: 'reference',
            header: 'Reference',
            enableSorting: false,
        },
        {
            accessorKey: 'origin',
            header: 'Origin',
            meta: {
                innerHtml: ({ row }: CellContext<ShippingPortalDataItem, unknown>) => {
                    const { origin } = row.original;
                    return formatEndpointAddressHtml(origin, true);
                },
                filterPlaceholder: 'Location Code...',
                sortingOptionName: 'origin',
            },
        },
        {
            accessorKey: 'destination',
            header: 'Destination',
            meta: {
                innerHtml: ({ row }: CellContext<ShippingPortalDataItem, unknown>) => {
                    const { destination } = row.original;
                    return formatEndpointAddressHtml(destination, true);
                },
                filterPlaceholder: 'Location Code...',
                sortingOptionName: 'destination',
            },
        },
        {
            accessorKey: 'createdBy',
            header: 'Created By',
            meta: {
                sortingOptionName: 'createdBy',
            },
        },
        {
            accessorKey: 'createdOn',
            header: 'Created On',
            meta: {
                innerHtml: ({ getValue }: CellContext<ShippingPortalDataItem, unknown>) => {
                    {
                        return getValue() ? (dayjs(getValue() as string).format(DateFormats.ShortDate) as ReactNode) : '';
                    }
                },
                filterPlaceholder: `e.g. ${dayjs().format(DateFormats.IsoDate)}`,
                sortingOptionName: 'createdOn',
            },
        },
        {
            accessorKey: 'departureDate',
            header: 'Shipment Date',
            meta: {
                innerHtml: ({ getValue }: CellContext<ShippingPortalDataItem, unknown>): React.ReactNode => {
                    {
                        return getValue() ? (dayjs(getValue() as string).format(DateFormats.ShortDate) as ReactNode) : '';
                    }
                },
                filterPlaceholder: `e.g. ${dayjs().format(DateFormats.IsoDate)}`,
                sortingOptionName: 'departureDate',
            },
        },
    ];

    const getHeaderText = () => {
        const subPage = PAGE_INFO.shippingPortal.subPages?.find((page) => page.name === shippingType);
        if (subPage) {
            return subPage.displayText;
        } else {
            return '';
        }
    };

    return (
        <Box id="shipping-portal-container" className="auto-fill-height">
            <Box id="transfers-returns-container" className="auto-fill-height">
                <Card className="transfers-returns-card auto-fill-height">
                    <Grid container spacing={2}>
                        <Grid xs={12}>
                            <Header text={getHeaderText()} />
                        </Grid>
                        <Grid xs={12}>
                            First, create a new transfer or return label. Then, you may select one or more unmanifested labels,
                            to generate a manifest. Manifests can be voided from within their detail screens. Labels can only be
                            printed after they have been included in a manifest. Only unmanifested labels can be deleted. You
                            can sort or filter by any of the columns by clicking on them.
                        </Grid>

                        <Grid xs={12} container display="flex" alignItems="flex-end">
                            <Grid>
                                <Button variant="contained" size="medium" onClick={handleCreateLabelClick}>
                                    {`Create New ${getCapitalized(shippingType)}`}
                                </Button>
                            </Grid>
                            <Grid>
                                <Button variant="contained" size="medium" onClick={onClickCreateManifest}>
                                    New Manifest
                                </Button>
                            </Grid>
                            <Grid>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={onClickPrintLabels}
                                    disabled={isGeneratingLabel}
                                >
                                    {isGeneratingLabel ? 'Generating...' : 'Print Labels'}
                                </Button>
                            </Grid>
                            <Grid>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={onClickPrintManifests}
                                    disabled={isGeneratingManifest}
                                >
                                    {isGeneratingManifest ? 'Generating...' : 'Print Manifests'}
                                </Button>
                            </Grid>
                            <Grid>
                                <Button variant="contained" size="medium" onClick={onDeleteLabels}>
                                    Delete Labels
                                </Button>
                            </Grid>
                            <Grid>
                                <Button variant="text" size="medium" onClick={onClickClearFilters}>
                                    Clear Filters
                                </Button>
                            </Grid>
                            <Grid xs={3}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    checked={hideUnmanifestedParcels}
                                    onChange={onHideUnmanifestedParcelsChange}
                                />
                                Hide Unmanifested Parcels
                            </Grid>

                            <Grid sm={3.15} xs={12} justifySelf="flex-end">
                                <FormControl fullWidth>
                                    <Autocomplete
                                        id="customerId"
                                        options={customers.map((customer) => ({
                                            label: customer.customerName,
                                            id: customer.customerId,
                                        }))}
                                        defaultValue={allCustomersValue}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                name="customer"
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {customersAreLoading ? (
                                                                <CircularProgress color="inherit" size={20} />
                                                            ) : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                                InputLabelProps={{ ...params.InputLabelProps }}
                                            />
                                        )}
                                        value={selectedCustomer}
                                        onChange={(event, value) => onChangeCustomer(value)}
                                        disabled={customersAreLoading}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>
                    <CardContent className="table-card auto-fill-height">
                        <FmtpTable
                            isFetching={getIsFetching()}
                            data={flatData}
                            virtualizerSettings={{ debug: true }}
                            columns={manifestColumns}
                            setSelectedRows={setSelectedRows}
                            selectedRows={selectedRows}
                            enableRowSelection={enableRowSelection}
                            fetchMoreOnBottomReached={fetchMoreOnBottomReached}
                            onFilterChange={onFilterChange}
                            onSortChange={onSortChange}
                            onExpandedChange={onExpandedChange}
                            clearFilters={clearFilters}
                            getSubRows={getSubRows}
                            expanded={expandedRows}
                            enableExpanding={true}
                            enableSorting={true}
                            isResizable={false}
                            enableFilters={true}
                            manualExpanding={true}
                            clearSelections={clearSelections}
                            isVirtualized={false}
                            initialSortingState={[
                                {
                                    id: initialFilters.sortOrder.column,
                                    desc: initialFilters.sortOrder.order === SortDirection.DESCENDING,
                                },
                            ]}
                            createExpandRows={createExpandRows}
                            showTable={flatData.length > 0}
                        />
                    </CardContent>
                </Card>
                <Dialog open={deleteLabelsDialogOpen} onClose={() => setDeleteLabelsDialogOpen(false)}>
                    <DialogTitle>Delete Labels</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to delete the selected labels? This cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteLabelsDialogOpen(false)}>Cancel</Button>
                        <Button variant="contained" color="primary" onClick={handleConfirmDeleteLabels}>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}
